// Hook to fetch billing data (bills, payments, time entries, expenses, trust)
// scoped to a single client across ALL their matters.
// Mirrors useMatterBilling but uses client_id instead of matter_id.

import { useState, useEffect, useCallback, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import type {
  Expense,
  TimeEntry,
  Bill,
  ClientTrustLedger,
  TrustTransaction,
  Payment,
} from '../../billing/types/billing.types';

const API_BASE = '';

export interface ClientBillingStats {
  // Expense stats
  totalExpenses: number;
  unbilledExpenses: number;
  billedExpenses: number;
  expenseCount: number;
  // Time entry stats
  totalTimeAmount: number;
  totalHours: number;
  billableHours: number;
  unbilledTimeAmount: number;
  billedTimeAmount: number;
  timeEntryCount: number;
  // Bill stats
  billsTotal: number;
  billsOutstanding: number;
  billsPaid: number;
  billCount: number;
  // Trust stats
  trustBalance: number;
  trustTotalDeposits: number;
  trustTotalDisbursements: number;
  trustTransactionCount: number;
  // Combined
  totalWip: number;
  totalBilled: number;
  // Client-specific: AR aging
  arCurrent: number;
  ar31to60: number;
  ar61to90: number;
  ar91Plus: number;
  // Client-specific: collection rate
  collectionRate: number;
  // Client-specific: matter count
  matterCount: number;
}

const EMPTY_STATS: ClientBillingStats = {
  totalExpenses: 0,
  unbilledExpenses: 0,
  billedExpenses: 0,
  expenseCount: 0,
  totalTimeAmount: 0,
  totalHours: 0,
  billableHours: 0,
  unbilledTimeAmount: 0,
  billedTimeAmount: 0,
  timeEntryCount: 0,
  billsTotal: 0,
  billsOutstanding: 0,
  billsPaid: 0,
  billCount: 0,
  trustBalance: 0,
  trustTotalDeposits: 0,
  trustTotalDisbursements: 0,
  trustTransactionCount: 0,
  totalWip: 0,
  totalBilled: 0,
  arCurrent: 0,
  ar31to60: 0,
  ar61to90: 0,
  ar91Plus: 0,
  collectionRate: 0,
  matterCount: 0,
};

/** Compute days overdue from a due_date string */
function daysOverdue(dueDate: string): number {
  const due = new Date(dueDate);
  const now = new Date();
  const diff = now.getTime() - due.getTime();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
}

export function useClientBilling(clientId: string) {
  const { keycloak } = useKeycloak();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [bills, setBills] = useState<Bill[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [trustLedgers, setTrustLedgers] = useState<ClientTrustLedger[]>([]);
  const [trustTransactions, setTrustTransactions] = useState<TrustTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<ClientBillingStats>(EMPTY_STATS);

  const clientIdRef = useRef(clientId);
  clientIdRef.current = clientId;

  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${keycloak?.token || ''}`,
  }), [keycloak?.token]);

  const fetchAll = useCallback(async () => {
    if (!clientId || !keycloak?.token) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ client_id: clientId });
      const headers = getHeaders();

      const [expRes, teRes, billRes, paymentRes, trustLedgerRes, trustTxnRes] = await Promise.all([
        fetch(`${API_BASE}/api/v1/billing/expenses?${params}`, { headers }),
        fetch(`${API_BASE}/api/v1/billing/time-entries?${params}`, { headers }),
        fetch(`${API_BASE}/api/v1/billing/bills?${params}`, { headers }),
        fetch(`${API_BASE}/api/v1/billing/payments?${params}`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/v1/billing/trust-accounts/ledgers/all?${params}`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/v1/billing/trust-transactions?${params}`, { headers }).catch(() => null),
      ]);

      if (!expRes.ok) throw new Error(`Failed to fetch expenses: ${expRes.statusText}`);
      if (!teRes.ok) throw new Error(`Failed to fetch time entries: ${teRes.statusText}`);
      if (!billRes.ok) throw new Error(`Failed to fetch bills: ${billRes.statusText}`);

      const [expJson, teJson, billJson] = await Promise.all([
        expRes.json(),
        teRes.json(),
        billRes.json(),
      ]);

      const paymentJson = paymentRes?.ok ? await paymentRes.json() : null;
      const trustLedgerJson = trustLedgerRes?.ok ? await trustLedgerRes.json() : null;
      const trustTxnJson = trustTxnRes?.ok ? await trustTxnRes.json() : null;

      if (clientIdRef.current !== clientId) return;

      const expData: Expense[] = expJson.data || [];
      const teData: TimeEntry[] = teJson.data || [];
      const billData: Bill[] = billJson.data || [];
      const paymentData: Payment[] = paymentJson?.data?.payments || paymentJson?.data || [];
      const trustLedgerData: ClientTrustLedger[] = trustLedgerJson?.data || [];
      const trustTxnData: TrustTransaction[] = trustTxnJson?.data || [];

      setExpenses(expData);
      setTimeEntries(teData);
      setBills(billData);
      setPayments(paymentData);
      setTrustLedgers(trustLedgerData);
      setTrustTransactions(trustTxnData);

      // Compute stats
      const totalExpenses = expData.reduce((s, e) => s + Number(e.total_amount), 0);
      const billedExpenses = expData.filter(e => e.is_billed).reduce((s, e) => s + Number(e.total_amount), 0);
      const unbilledExpenses = expData.filter(e => !e.is_billed && !e.is_non_billable).reduce((s, e) => s + Number(e.total_amount), 0);

      const totalTimeAmount = teData.reduce((s, t) => s + Number(t.amount), 0);
      const totalHours = teData.reduce((s, t) => s + Number(t.duration), 0);
      const billableHours = teData.filter(t => !t.is_non_billable).reduce((s, t) => s + Number(t.duration), 0);
      const billedTimeAmount = teData.filter(t => t.is_billed).reduce((s, t) => s + Number(t.amount), 0);
      const unbilledTimeAmount = teData.filter(t => !t.is_billed && !t.is_non_billable).reduce((s, t) => s + Number(t.amount), 0);

      const billsTotal = billData.reduce((s, b) => s + Number(b.total), 0);
      const billsOutstanding = billData.filter(b => Number(b.balance) > 0 && b.status !== 'cancelled').reduce((s, b) => s + Number(b.balance), 0);
      const billsPaid = billData.reduce((s, b) => s + Number(b.amount_paid), 0);

      // Trust stats
      const trustBalance = trustLedgerData.reduce((s, l) => s + Number(l.current_balance), 0);
      const trustTotalDeposits = trustLedgerData.reduce((s, l) => s + Number(l.total_deposits), 0);
      const trustTotalDisbursements = trustLedgerData.reduce((s, l) => s + Number(l.total_disbursements), 0);

      // AR aging — bucket outstanding bills by days overdue
      let arCurrent = 0, ar31to60 = 0, ar61to90 = 0, ar91Plus = 0;
      for (const bill of billData) {
        const outstanding = Number(bill.balance);
        if (outstanding <= 0 || bill.status === 'cancelled' || bill.status === 'paid') continue;
        const days = bill.due_date ? daysOverdue(bill.due_date) : 0;
        if (days <= 30) arCurrent += outstanding;
        else if (days <= 60) ar31to60 += outstanding;
        else if (days <= 90) ar61to90 += outstanding;
        else ar91Plus += outstanding;
      }

      // Collection rate
      const collectionRate = billsTotal > 0 ? (billsPaid / billsTotal) * 100 : 0;

      // Distinct matter count across all billing data
      const matterIds = new Set<string>();
      billData.forEach(b => { if (b.matter_id) matterIds.add(b.matter_id); });
      teData.forEach(t => { if (t.matter_id) matterIds.add(t.matter_id); });
      expData.forEach(e => { if (e.matter_id) matterIds.add(e.matter_id); });

      setStats({
        totalExpenses,
        unbilledExpenses,
        billedExpenses,
        expenseCount: expData.length,
        totalTimeAmount,
        totalHours,
        billableHours,
        unbilledTimeAmount,
        billedTimeAmount,
        timeEntryCount: teData.length,
        billsTotal,
        billsOutstanding,
        billsPaid,
        billCount: billData.length,
        trustBalance,
        trustTotalDeposits,
        trustTotalDisbursements,
        trustTransactionCount: trustTxnData.length,
        totalWip: unbilledTimeAmount + unbilledExpenses,
        totalBilled: billedTimeAmount + billedExpenses,
        arCurrent,
        ar31to60,
        ar61to90,
        ar91Plus,
        collectionRate,
        matterCount: matterIds.size,
      });
    } catch (err) {
      if (clientIdRef.current !== clientId) return;
      const msg = err instanceof Error ? err.message : 'Failed to fetch billing data';
      setError(msg);
    } finally {
      if (clientIdRef.current === clientId) {
        setLoading(false);
      }
    }
  }, [clientId, keycloak?.token, getHeaders]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return {
    expenses,
    timeEntries,
    bills,
    payments,
    trustLedgers,
    trustTransactions,
    loading,
    error,
    stats,
    refresh: fetchAll,
  };
}
